import { useNavigate } from "react-router-dom";
import { MapPin, Search, CalendarCheck, Building2, Users, ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import BoroughLinks from "@/components/BoroughLinks";
import { SITE_CONFIG, HOW_IT_WORKS } from "@/data/siteConfig";
import { PRACTICES } from "@/data/practices";

const iconMap = { MapPin, Search, CalendarCheck } as const;

const Index = () => {
  const navigate = useNavigate();

  const handleSearch = (postcode: string) => {
    navigate(`/search?postcode=${encodeURIComponent(postcode)}`);
  };

  const acceptingCount = PRACTICES.filter(p => p.acceptingNewPatients === "yes" || p.acceptingNewPatients === "limited").length;
  const acceptingPct = Math.round((acceptingCount / PRACTICES.length) * 100);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />

      <main>
        {/* Stats Banner */}
        <div className="bg-accent/10 border-b border-accent/20">
          <div className="mx-auto flex max-w-5xl flex-col items-start gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-center sm:gap-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Building2 className="h-4 w-4 shrink-0 text-primary" />
              <span>Over <span className="text-primary">{PRACTICES.length}</span> NHS practices now listed across London &amp; major UK cities</span>
            </div>
            <span className="hidden h-4 w-px bg-border sm:block" />
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Users className="h-4 w-4 shrink-0 text-primary" />
              <span><span className="text-primary">{acceptingPct}%</span> currently accepting new patients</span>
            </div>
          </div>
        </div>

        {/* Hero */}
        <section className="relative overflow-hidden px-4 py-20 sm:py-28" style={{ background: "var(--gradient-hero)" }}>
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-extrabold tracking-tight text-primary-foreground sm:text-5xl lg:text-6xl">
              {SITE_CONFIG.tagline}
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-lg text-primary-foreground/80">
              {SITE_CONFIG.description}
            </p>
            <div className="mt-8">
              <SearchBar onSearch={handleSearch} variant="hero" />
            </div>
          </div>
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute -left-40 -top-40 h-96 w-96 rounded-full bg-primary-foreground/5 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-primary-foreground/5 blur-3xl" />
        </section>

        {/* How It Works */}
        <section className="bg-muted px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-center text-2xl font-bold text-foreground sm:text-3xl">
              How It Works
            </h2>
            <div className="mt-10 grid gap-8 sm:grid-cols-3">
              {HOW_IT_WORKS.map((step) => {
                const Icon = iconMap[step.icon];
                return (
                  <div key={step.step} className="flex flex-col items-center text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-foreground">{step.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{step.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Browse by Area */}
        <section className="px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <BoroughLinks limit={20} />
          </div>
        </section>

        {/* Pharmacy First info box */}
        <section className="px-4 py-10 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl rounded-lg border border-border/60 bg-muted/50 px-5 py-5 sm:px-6 sm:py-6">
            <h3 className="text-sm font-semibold text-foreground">
              Pharmacy First – Get help faster for minor conditions
            </h3>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
              Many common issues (sore throat, sinusitis, UTI in women, impetigo, shingles, earache, insect bites) can be treated at a pharmacy without seeing a GP. Search for a nearby pharmacy and look for Pharmacy First eligible options.
            </p>
            <a
              href="https://www.nhs.uk/nhs-services/pharmacies/how-pharmacies-can-help/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              Learn more on NHS.uk <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
